import { ConnectorTemplate, ConnectorFieldMapping } from "../../types"

/**
 * Predefined connector templates for common CRM integrations
 * These provide smart defaults that users can customize
 */

// Lead field mappings
export const ZAPIER_LEAD_MAPPING: ConnectorFieldMapping = {
  first_name: "details.first_name",
  last_name: "details.last_name",
  email: "email",
  phone: "phone",
  loan_purpose: "details.loan_purpose",
  loan_type: "details.loan_type",
  loan_amount: "details.loan_amount",
  credit_score: "details.credit_score",
  city: "details.location.city",
  state: "details.location.state",
  zipcode: "details.location.zipcode",
  message: "details.message"
}

export const GENERIC_LEAD_MAPPING: ConnectorFieldMapping = {
  firstname: "details.first_name",
  lastname: "details.last_name",
  email: "email",
  phone: "phone",
  creditscore: "details.credit_score",
  downpayment: "details.down_payment",
  listprice: "details.list_price",
  loanamount: "details.loan_amount",
  loanpurpose: "details.loan_purpose",
  loantype: "details.loan_type",
  loanterm: "details.loan_term",
  propertytype: "details.property_type",
  residencytype: "details.residency_type",
  realtor: "details.realtor",
  city: "details.location.city",
  state: "details.location.state",
  zipcode: "details.location.zipcode",
  streetaddress: "details.location.street_address",
  county: "details.location.county",
  message: "details.message"
}

// Subscription field mappings
export const ZAPIER_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  phone: "phone",
  email: "email",
  loid: "loid",
  first_name: "first_name",
  last_name: "last_name",
  loan_purpose: "quote_info.loan_purpose",
  credit_score: "quote_info.credit_score",
  property_type: "quote_info.property_type",
  residency_type: "quote_info.residency_type",
  list_price: "quote_info.list_price",
  loan_amount: "quote_info.loan_amount",
  loan_term: "quote_info.loan_term",
  loan_type: "quote_info.loan_type",
  state: "quote_info.location.state",
  city: "quote_info.location.city",
  zipcode: "quote_info.location.zipcode",
  street_address: "quote_info.location.street_address",
  county: "quote_info.location.county"
}

// Pending subscription field mappings
export const ZAPIER_PENDING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = {
  phone: "phone",
  email: "email",
  loid: "loid",
  first_name: "first_name",
  last_name: "last_name",
  trigger_rate: "trigger_rate",
  loan_purpose: "quote_info.loan_purpose",
  credit_score: "quote_info.credit_score",
  property_type: "quote_info.property_type",
  residency_type: "quote_info.residency_type",
  list_price: "quote_info.list_price",
  loan_amount: "quote_info.loan_amount",
  loan_term: "quote_info.loan_term",
  loan_type: "quote_info.loan_type",
  state: "quote_info.location.state",
  city: "quote_info.location.city",
  zipcode: "quote_info.location.zipcode",
  street_address: "quote_info.location.street_address",
  county: "quote_info.location.county"
}

// Template definitions
export const CONNECTOR_TEMPLATES: ConnectorTemplate[] = [
  {
    name: "Zapier - Lead (Standard)",
    type: "zapier",
    data_type: "lead",
    field_mapping: ZAPIER_LEAD_MAPPING,
    description:
      "Standard field mapping for Zapier lead webhooks with camelCase field names"
  },
  {
    name: "Generic Webhook - Lead (Lowercase)",
    type: "webhook",
    data_type: "lead",
    field_mapping: GENERIC_LEAD_MAPPING,
    description:
      "Generic webhook mapping for leads with lowercase, no-underscore field names (common in many CRMs)"
  },
  {
    name: "Zapier - Subscription",
    type: "zapier",
    data_type: "subscription",
    field_mapping: ZAPIER_SUBSCRIPTION_MAPPING,
    description: "Standard field mapping for Zapier subscription webhooks"
  },
  {
    name: "Zapier - Pending Subscription",
    type: "zapier",
    data_type: "pending_subscription",
    field_mapping: ZAPIER_PENDING_SUBSCRIPTION_MAPPING,
    description:
      "Standard field mapping for Zapier pending subscription webhooks"
  }
]

/**
 * Get available templates filtered by data type
 */
export const getTemplatesByDataType = (
  dataType: "lead" | "subscription" | "pending_subscription"
): ConnectorTemplate[] => {
  return CONNECTOR_TEMPLATES.filter(t => t.data_type === dataType)
}

/**
 * Get template by name
 */
export const getTemplateByName = (
  name: string
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(t => t.name === name)
}

/**
 * Get default template for a given CRM type and data type
 */
export const getDefaultTemplate = (
  crmType: string,
  dataType: "lead" | "subscription" | "pending_subscription"
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(
    t => t.type === crmType && t.data_type === dataType
  )
}

// Field reference documentation for help text
export const LEAD_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "details.first_name", label: "First Name", category: "Basic" },
  { value: "details.last_name", label: "Last Name", category: "Basic" },
  {
    value: "details.loan_purpose",
    label: "Loan Purpose",
    category: "Loan Info"
  },
  { value: "details.loan_type", label: "Loan Type", category: "Loan Info" },
  { value: "details.loan_amount", label: "Loan Amount", category: "Loan Info" },
  { value: "details.loan_term", label: "Loan Term", category: "Loan Info" },
  {
    value: "details.credit_score",
    label: "Credit Score",
    category: "Loan Info"
  },
  {
    value: "details.down_payment",
    label: "Down Payment",
    category: "Loan Info"
  },
  { value: "details.list_price", label: "List Price", category: "Loan Info" },
  {
    value: "details.property_type",
    label: "Property Type",
    category: "Property"
  },
  {
    value: "details.residency_type",
    label: "Residency Type",
    category: "Property"
  },
  { value: "details.realtor", label: "Realtor", category: "Property" },
  { value: "details.location.city", label: "City", category: "Location" },
  { value: "details.location.state", label: "State", category: "Location" },
  {
    value: "details.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "details.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  { value: "details.location.county", label: "County", category: "Location" },
  { value: "details.message", label: "Message", category: "Other" }
]

export const SUBSCRIPTION_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "first_name", label: "First Name", category: "Basic" },
  { value: "last_name", label: "Last Name", category: "Basic" },
  { value: "loid", label: "Loan Officer ID", category: "Basic" },
  { value: "type", label: "Type", category: "Basic" },
  { value: "active", label: "Active", category: "Basic" },
  {
    value: "quote_info.loan_purpose",
    label: "Loan Purpose",
    category: "Quote Info"
  },
  {
    value: "quote_info.credit_score",
    label: "Credit Score",
    category: "Quote Info"
  },
  {
    value: "quote_info.property_type",
    label: "Property Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.residency_type",
    label: "Residency Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.list_price",
    label: "List Price",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_amount",
    label: "Loan Amount",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_term",
    label: "Loan Term",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_type",
    label: "Loan Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.location.state",
    label: "State",
    category: "Location"
  },
  { value: "quote_info.location.city", label: "City", category: "Location" },
  {
    value: "quote_info.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "quote_info.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  {
    value: "quote_info.location.county",
    label: "County",
    category: "Location"
  }
]

export const PENDING_SUBSCRIPTION_FIELD_OPTIONS = [
  ...SUBSCRIPTION_FIELD_OPTIONS,
  { value: "trigger_rate", label: "Trigger Rate", category: "Basic" }
]
